import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, MapPin, MessageSquare, Search } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "./Community.css";
import {
  pickCommunityBio,
  pickCommunityName,
  pickCommunityRecipientId,
  useCommunityData,
  validateCommunityDiscussionTopic,
} from "./community.functions";
import { DropdownField, DualRangeSlider } from "../../components/forms";
import Button from "../../components/primitives/button";
import Input from "../../components/primitives/input";
import { computePaginationRange } from "../../components/conversation-list/pagination.functions";
import { US_STATES } from "../../domain/profile-options/states";
import { POLITICAL_AFFILIATION } from "../../domain/profile-options/political-affiliation";
import { RELIGIOUS_AFFILIATION } from "../../domain/profile-options/religious-affiliation";
import { GENDERS } from "../../domain/profile-options/demographics";
import { ProfileDialog, ReportDialog } from "../../components/dialogs";
import { ProfileAvatar } from "../../components/profile-display";
import { createUserReport } from "../../api/reporting/reporting-service";
import {
  consumePendingMessageIntent,
  savePendingMessageIntent,
} from "../../client-state/session/pending-message-intent-service";
import { evaluateClientTextModeration } from "../../api/moderation/content-moderation-service";
import { subscribeCommunicationEvents } from "../../api/communication";
import { toast } from "../../components/toast";

const DISCUSSION_TOPIC_MAX_LENGTH = 200;
const COMMUNITY_RESULTS_PAGE_SIZE = 12;

export default function Community({ isGuestPreview = false }) {
  const navigate = useNavigate();
  const stateFilterOptions = useMemo(
    () => US_STATES.filter((option) => String(option?.value || "").toUpperCase() !== "ANY"),
    []
  );
  const politicalAffiliationFilterOptions = useMemo(
    () =>
      POLITICAL_AFFILIATION.filter(
        (option) => {
          const normalizedValue = String(option?.value || "").trim().toLowerCase();
          return normalizedValue !== "prefer not to say" && normalizedValue !== "any";
        }
      ),
    []
  );
  const religiousAffiliationFilterOptions = useMemo(
    () =>
      RELIGIOUS_AFFILIATION.filter(
        (option) => {
          const normalizedValue = String(option?.value || "").trim().toLowerCase();
          return normalizedValue !== "prefer not to say" && normalizedValue !== "any";
        }
      ),
    []
  );
  const {
    loading,
    loadingMore,
    hasMoreSearchResults,
    loadMoreSearchResults,
    results,
    blockedUserIds,
    sociopoliticalFilters,
    sociopoliticalQuestions,
    handleIrrelevantToggle,
    handleSociopoliticalChange,
    setAllSociopoliticalRelevance,
    filters,
    handleFilterChange,
    interestOptions,
    handleInterestToggle,
    runSearch,
    resetFilters,
    blockUser,
    unblockUser,
    initiateMessageRequest,
    resolvePairChatState,
    calculateAge,
  } = useCommunityData({ isGuest: isGuestPreview });

  // State for profile modal
  const pendingIntentHydratedRef = useRef(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messageTargetId, setMessageTargetId] = useState("");
  const [pairChatStateByRecipient, setPairChatStateByRecipient] = useState({});
  const [selectedRecipientIds, setSelectedRecipientIds] = useState([]);
  const [batchState, setBatchState] = useState({
    isSending: false,
    total: 0,
    completed: 0,
    succeeded: 0,
    failed: 0,
    currentRecipientId: "",
  });
  const [batchFailures, setBatchFailures] = useState([]);
  const [batchSummary, setBatchSummary] = useState(null);
  const [requestComposer, setRequestComposer] = useState(null);
  const [discussionTopicInput, setDiscussionTopicInput] = useState("");
  const [topicError, setTopicError] = useState("");
  const [composerSending, setComposerSending] = useState(false);
  const [blockActionTargetId, setBlockActionTargetId] = useState("");
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportTarget, setReportTarget] = useState(null);
  const [cardLayoutMode, setCardLayoutMode] = useState("grid");
  const [currentPage, setCurrentPage] = useState(1);
  const [walkthroughStepId, setWalkthroughStepId] = useState(null);
  const [isWalkthroughActive, setIsWalkthroughActive] = useState(false);
  const hasDispatchedMessageEnteredRef = useRef(false);
  const isWalkthroughBlockingSend =
    !isGuestPreview && isWalkthroughActive && walkthroughStepId !== "request-send";
  const isWalkthroughRestrictedToSendRequest =
    !isGuestPreview && isWalkthroughActive && walkthroughStepId === "send-request";

  useEffect(() => {
    const handleStep = (event) => {
      setWalkthroughStepId(event?.detail?.stepId || null);
    };
    window.addEventListener("walkthrough:step", handleStep);
    return () => window.removeEventListener("walkthrough:step", handleStep);
  }, []);

  useEffect(() => {
    const handleState = (event) => {
      setIsWalkthroughActive(Boolean(event?.detail?.active));
    };
    window.addEventListener("walkthrough:state", handleState);
    return () => window.removeEventListener("walkthrough:state", handleState);
  }, []);

  useEffect(() => {
    const handleCompleted = () => {
      setRequestComposer(null);
      setTopicError("");
      hasDispatchedMessageEnteredRef.current = false;
    };
    window.addEventListener("walkthrough:completed", handleCompleted);
    return () => window.removeEventListener("walkthrough:completed", handleCompleted);
  }, []);

  useEffect(() => {
    runSearch({ defaultDiscovery: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dispatchWalkthroughAction = (action) => {
    try {
      window.dispatchEvent(new CustomEvent("walkthrough:action", { detail: { action } }));
    } catch (error) {
      // Ignore if CustomEvent is unavailable.
    }
  };

  const selectableUsers = useMemo(() => {
    const userMap = new Map();
    for (const user of results) {
      const recipientId = pickCommunityRecipientId(user);
      if (!recipientId || userMap.has(recipientId)) continue;
      userMap.set(recipientId, user);
    }
    return userMap;
  }, [results]);

  useEffect(() => {
    if (isGuestPreview) {
      setPairChatStateByRecipient({});
      return () => {};
    }

    const recipientIds = [
      ...new Set(results.map((user) => pickCommunityRecipientId(user)).filter(Boolean)),
    ];
    if (!recipientIds.length) {
      setPairChatStateByRecipient({});
      return () => {};
    }

    let cancelled = false;

    async function loadPairStates() {
      const entries = await Promise.all(
        recipientIds.map(async (recipientId) => {
          try {
            const pairState = await resolvePairChatState(recipientId);
            return [recipientId, pairState];
          } catch (error) {
            console.warn("Unable to resolve pair chat state for connect card:", {
              recipientId,
              error,
            });
            return [recipientId, null];
          }
        })
      );

      if (cancelled) return;

      const nextState = {};
      entries.forEach(([recipientId, pairState]) => {
        if (!recipientId || !pairState?.chatPk) return;
        const status = String(pairState.status || "").trim().toLowerCase();
        if (status !== "accepted" && status !== "pending") return;
        nextState[recipientId] = {
          chatPk: pairState.chatPk,
          status,
        };
      });

      setPairChatStateByRecipient(nextState);
    }

    loadPairStates();
    return () => {
      cancelled = true;
    };
  }, [isGuestPreview, results, resolvePairChatState]);

  const batchPercent = batchState.total
    ? Math.round((batchState.completed / batchState.total) * 100)
    : 0;
  const selectedCount = selectedRecipientIds.length;
  const sortedResults = useMemo(() => {
    return [...results].sort((left, right) => {
      const leftRecipientId = pickCommunityRecipientId(left);
      const rightRecipientId = pickCommunityRecipientId(right);
      const leftHasExisting = Boolean(leftRecipientId && pairChatStateByRecipient[leftRecipientId]);
      const rightHasExisting = Boolean(rightRecipientId && pairChatStateByRecipient[rightRecipientId]);

      if (leftHasExisting !== rightHasExisting) {
        return leftHasExisting ? 1 : -1;
      }

      return pickCommunityName(left).localeCompare(pickCommunityName(right));
    });
  }, [pairChatStateByRecipient, results]);
  const totalResults = results.length;
  const {
    totalPages,
    rangeStart: resultsRangeStart,
    rangeEnd: resultsRangeEnd,
  } = computePaginationRange({
    currentPage,
    pageSize: COMMUNITY_RESULTS_PAGE_SIZE,
    totalItems: totalResults,
  });
  const paginatedResults = useMemo(() => {
    const startIndex = (currentPage - 1) * COMMUNITY_RESULTS_PAGE_SIZE;
    return sortedResults.slice(startIndex, startIndex + COMMUNITY_RESULTS_PAGE_SIZE);
  }, [currentPage, sortedResults]);
  const resultsGridRef = useRef(null);

  useEffect(() => {
    if (totalPages <= 1) return;
    const node = resultsGridRef.current;
    if (node && typeof node.scrollIntoView === "function") {
      node.scrollIntoView({ block: "start", behavior: "smooth" });
    }
  }, [currentPage, totalPages]);
  const topicModeration = useMemo(
    () =>
      evaluateClientTextModeration({
        fieldType: "topic",
        text: discussionTopicInput,
      }),
    [discussionTopicInput]
  );

  const openProfile = (user) => {
    if (isWalkthroughRestrictedToSendRequest) return;
    setSelectedUser(user);
  };

  const handleCardClick = (event, user) => {
    if (isWalkthroughRestrictedToSendRequest) return;
    const interactiveTarget = event.target.closest(
      "button, a, input, select, textarea, label, [role='button'], .communityBrowse__cardBtn"
    );
    if (interactiveTarget) return;

    openProfile(user);
  };

  const closeProfile = () => {
    setSelectedUser(null);
  };

  const openReportForSelectedUser = () => {
    const targetId = pickCommunityRecipientId(selectedUser);
    if (!targetId) return;

    setReportTarget({
      targetUserId: targetId,
      targetLabel: pickCommunityName(selectedUser),
      sourceSurface: "PROFILE",
    });
    setReportDialogOpen(true);
  };

  const closeReportDialog = () => {
    setReportDialogOpen(false);
    setReportTarget(null);
  };

  const submitUserReport = async ({ reasonCode, reasonText }) => {
    if (!reportTarget?.targetUserId) {
      throw new Error("Report target is missing.");
    }

    await createUserReport({
      targetType: "USER",
      targetUserId: reportTarget.targetUserId,
      reasonCode,
      reasonText,
      sourceSurface: reportTarget.sourceSurface || "PROFILE",
    });
    toast.success("Report submitted. Our admins will review it.");
  };

  useEffect(() => {
    setSelectedRecipientIds((prev) =>
      prev.filter(
        (recipientId) =>
          selectableUsers.has(recipientId) &&
          !pairChatStateByRecipient[recipientId]
      )
    );
  }, [pairChatStateByRecipient, selectableUsers]);

  useEffect(() => {
    if (isGuestPreview) return () => {};

    const unsubscribe = subscribeCommunicationEvents((payload) => {
      const type = String(payload?.type || "").trim();
      const chatPk = String(payload?.chatPk || "").trim();
      const nextStatus = String(payload?.status || "").trim().toLowerCase();

      if (!chatPk) return;
      if (
        type !== "chat_request_updated" &&
        type !== "chat_deleted" &&
        type !== "chat_visibility_changed"
      ) {
        return;
      }

      setPairChatStateByRecipient((previous) => {
        const entries = Object.entries(previous);
        const matchedEntry = entries.find(([, value]) => String(value?.chatPk || "") === chatPk);
        if (!matchedEntry) return previous;

        const [recipientId] = matchedEntry;

        if (
          type === "chat_deleted" ||
          nextStatus === "denied" ||
          nextStatus === "expired" ||
          nextStatus === "canceled" ||
          nextStatus === "cancelled"
        ) {
          const next = { ...previous };
          delete next[recipientId];
          return next;
        }

        if (nextStatus === "accepted" || nextStatus === "pending") {
          return {
            ...previous,
            [recipientId]: {
              chatPk,
              status: nextStatus,
            },
          };
        }

        return previous;
      });

      if (
        type === "chat_deleted" ||
        nextStatus === "denied" ||
        nextStatus === "expired" ||
        nextStatus === "canceled" ||
        nextStatus === "cancelled"
      ) {
        setSelectedRecipientIds((previous) =>
          previous.filter((recipientId) => {
            const state = pairChatStateByRecipient[recipientId];
            return String(state?.chatPk || "") !== chatPk;
          })
        );
      }
    });

    return () => unsubscribe?.();
  }, [isGuestPreview, pairChatStateByRecipient]);

  useEffect(() => {
    setCurrentPage(1);
  }, [results]);

  const openRequestComposer = (users, mode) => {
    if (batchState.isSending || composerSending) return;

    const dedupedUsers = [];
    const seen = new Set();
    for (const user of users || []) {
      const recipientId = pickCommunityRecipientId(user);
      if (!recipientId || seen.has(recipientId)) continue;
      seen.add(recipientId);
      dedupedUsers.push(user);
    }

    if (!dedupedUsers.length) {
      toast.error("No eligible recipients were found.");
      return;
    }

    setTopicError("");
    setDiscussionTopicInput("");
    setRequestComposer({
      mode,
      users: dedupedUsers,
    });
    hasDispatchedMessageEnteredRef.current = false;
    dispatchWalkthroughAction("composer-opened");
  };

  const closeRequestComposer = () => {
    if (composerSending) return;
    setRequestComposer(null);
    setTopicError("");
    hasDispatchedMessageEnteredRef.current = false;
  };

  const openDirectMessage = async (user) => {
    const targetId = pickCommunityRecipientId(user);
    if (!targetId) return;
    if (blockedUserIds.includes(targetId)) return;

    if (isGuestPreview) {
      savePendingMessageIntent({
        recipientId: targetId,
        recipientName: pickCommunityName(user),
        userSnapshot: {
          ...user,
          cognitoId: targetId,
          profileName: pickCommunityName(user),
        },
      });
      navigate("/active", { state: { source: "guest-browse-message" } });
      return;
    }

    openRequestComposer([user], "single");
  };

  const openExistingPairChat = async (user, pairState) => {
    const recipientId = pickCommunityRecipientId(user);
    if (!recipientId) return;

    let latestPairState = pairState;
    try {
      latestPairState = await resolvePairChatState(recipientId);
    } catch (error) {
      console.warn("Unable to refresh pair chat state before open:", {
        recipientId,
        error,
      });
    }

    const chatPk = String(latestPairState?.chatPk || "").trim();
    const status = String(latestPairState?.status || "").trim().toLowerCase();

    if (!chatPk || (status !== "accepted" && status !== "pending")) {
      setPairChatStateByRecipient((prev) => {
        if (!prev[recipientId]) return prev;
        const next = { ...prev };
        delete next[recipientId];
        return next;
      });
      openDirectMessage(user);
      return;
    }

    setPairChatStateByRecipient((prev) => ({
      ...prev,
      [recipientId]: { chatPk, status },
    }));

    // Open the chat in a new tab so the community browse stays put. See
    // Active.js goToChat for the rationale + popup-blocker fallback.
    const slug = encodeURIComponent(pickCommunityName(user) || "chat");
    const url = `/active/chat/${slug}?chatPk=${encodeURIComponent(chatPk)}`;
    const opened = window.open(url, "_blank", "noopener,noreferrer");
    if (!opened) {
      navigate(`/active/chat/${slug}`, {
        state: { selectedChatPk: chatPk },
      });
    }
  };

  useEffect(() => {
    if (isGuestPreview) return;
    if (pendingIntentHydratedRef.current) return;

    pendingIntentHydratedRef.current = true;
    const pendingIntent = consumePendingMessageIntent();
    if (!pendingIntent?.recipientId) return;

    const pendingUser = {
      ...(pendingIntent.userSnapshot || {}),
      cognitoId: pendingIntent.recipientId,
      profileName: pendingIntent.recipientName,
    };
    openRequestComposer([pendingUser], "single");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGuestPreview]);

  const toggleBlockForSelectedUser = async () => {
    const targetId = pickCommunityRecipientId(selectedUser);
    if (!targetId || blockActionTargetId) return;

    const currentlyBlocked = blockedUserIds.includes(targetId);
    setBlockActionTargetId(targetId);
    try {
      if (currentlyBlocked) {
        await unblockUser(targetId);
      } else {
        await blockUser(targetId);
        closeProfile();
      }
    } catch (error) {
      console.error("Failed to toggle block state:", error);
      toast.error(error?.message || "Failed to update block settings.");
    } finally {
      setBlockActionTargetId("");
    }
  };

  const toggleRecipientSelection = (recipientId) => {
    setSelectedRecipientIds((prev) =>
      prev.includes(recipientId)
        ? prev.filter((id) => id !== recipientId)
        : [...prev, recipientId]
    );
  };

  const clearSelection = () => {
    if (batchState.isSending) return;
    setSelectedRecipientIds([]);
  };

  const processBatchRequests = async (users, modeLabel, discussionTopic) => {
    if (batchState.isSending) return;

    const dedupedUsers = [];
    const seen = new Set();
    for (const user of users) {
      const recipientId = pickCommunityRecipientId(user);
      if (!recipientId || seen.has(recipientId)) continue;
      seen.add(recipientId);
      dedupedUsers.push(user);
    }

    if (!dedupedUsers.length) {
      toast.error("No eligible recipients were found.");
      return;
    }

    setBatchSummary(null);
    setBatchFailures([]);
    const total = dedupedUsers.length;

    setBatchState({
      isSending: true,
      total,
      completed: 0,
      succeeded: 0,
      failed: 0,
      currentRecipientId: "",
    });

    let completed = 0;
    let succeeded = 0;
    const failures = [];

    for (const user of dedupedUsers) {
      const recipientId = pickCommunityRecipientId(user);
      setBatchState({
        isSending: true,
        total,
        completed,
        succeeded,
        failed: failures.length,
        currentRecipientId: recipientId,
      });

      const response = await initiateMessageRequest(user, discussionTopic, {
        navigateOnSuccess: false,
        suppressErrorAlert: true,
      });

      completed += 1;
      if (response?.chatPk) {
        succeeded += 1;
      } else {
        failures.push({
          recipientId,
          recipientName: pickCommunityName(user),
          recipientUser: user,
          reason: "Unable to create chat request.",
        });
      }

      setBatchState({
        isSending: true,
        total,
        completed,
        succeeded,
        failed: failures.length,
        currentRecipientId: recipientId,
      });
    }

    setBatchState({
      isSending: false,
      total,
      completed,
      succeeded,
      failed: failures.length,
      currentRecipientId: "",
    });
    setBatchFailures(failures);
    setBatchSummary({
      mode: modeLabel,
      total: dedupedUsers.length,
      succeeded,
      failed: failures.length,
      completedAt: new Date().toISOString(),
    });

    if (failures.length > 0) {
      setSelectedRecipientIds(failures.map((entry) => entry.recipientId));
      return;
    }

    setSelectedRecipientIds([]);
  };

  const sendToSelected = async () => {
    if (!selectedRecipientIds.length) {
      toast.error("Select at least one person to send requests.");
      return;
    }
    const users = selectedRecipientIds
      .map((recipientId) => selectableUsers.get(recipientId))
      .filter((user) => {
        if (!user) return false;
        const recipientId = pickCommunityRecipientId(user);
        if (!recipientId) return false;
        return !pairChatStateByRecipient[recipientId];
      });
    if (!users.length) {
      toast.error("Selected users already have an existing chat or pending request.");
      return;
    }
    openRequestComposer(users, "selected");
  };

  const retryFailed = async () => {
    if (!batchFailures.length) return;
    const failedUsers = batchFailures
      .map((entry) => entry.recipientUser)
      .filter(Boolean);
    openRequestComposer(failedUsers, "retry");
  };

  const submitRequestComposer = async () => {
    if (!requestComposer || composerSending || batchState.isSending) return;
    if (isWalkthroughBlockingSend) return;

    const topicValidationError = validateCommunityDiscussionTopic(discussionTopicInput);
    if (topicValidationError) {
      setTopicError(topicValidationError);
      return;
    }

    const discussionTopic = discussionTopicInput.trim();
    const users = requestComposer.users || [];

    if (!users.length) {
      setTopicError("No recipients selected.");
      return;
    }

    setTopicError("");

    if (requestComposer.mode === "single" && users.length === 1) {
      const user = users[0];
      const targetId = pickCommunityRecipientId(user);
      if (!targetId) {
        setTopicError("Recipient is missing a valid Cognito ID.");
        return;
      }

      setComposerSending(true);
      setMessageTargetId(targetId);
      const response = await initiateMessageRequest(user, discussionTopic, {
        navigateOnSuccess: true,
        suppressErrorAlert: true,
      });
      setComposerSending(false);
      setMessageTargetId("");

      if (!response?.chatPk) {
        // Prefer the specific feedback string (e.g. moderation per-category
        // message) when initiateMessageRequest returns one; only fall back
        // to the generic copy if nothing was provided.
        setTopicError(
          response?.feedback ||
            "Failed to send the conversation request. Please try again."
        );
        return;
      }

      closeRequestComposer();
      return;
    }

    closeRequestComposer();
    await processBatchRequests(users, requestComposer.mode, discussionTopic);
  };

  return (
    <div className="communityBrowse">
      <div className="communityBrowse__container">
        <div className="communityBrowse__columns">
          {/* LEFT: Filters */}
          <aside className="communityBrowse__filters" data-walkthrough="community-filters">
            <h1 className="communityBrowse__filtersTitle">Who do you want to talk to?</h1>
            <div className="communityBrowse__form">
              <details
                className="communityBrowse__filterGroup"
                data-walkthrough="filter-group-identity"
              >
                <summary className="communityBrowse__filterGroupSummary">
                  <span className="communityBrowse__filterGroupTitle">Location & Identity</span>
                  <ChevronDown className="communityBrowse__filterGroupChevron" aria-hidden="true" />
                </summary>
                <div className="communityBrowse__filterGroupBody">
                  <div className="communityBrowse__field dropdown-field ">
                    <label className="communityBrowse__label">Profile Name</label>
                    <Input
                      name="username"
                      value={filters.username}
                      onChange={handleFilterChange}
                      placeholder="Search by username"
                    />
                  </div>

                  <DropdownField
                    label="State"
                    name="state"
                    value={filters.state}
                    onChange={handleFilterChange}
                    options={stateFilterOptions}
                    placeholder="All states"
                  />

                  <div className="communityBrowse__field dropdown-field ">
                    <label className="communityBrowse__label">City</label>
                    <Input
                      name="city"
                      value={filters.city}
                      onChange={handleFilterChange}
                      placeholder="Enter city name"
                    />
                  </div>
                </div>
              </details>

              <details className="communityBrowse__filterGroup">
                <summary className="communityBrowse__filterGroupSummary">
                  <span className="communityBrowse__filterGroupTitle">Demographics</span>
                  <ChevronDown className="communityBrowse__filterGroupChevron" aria-hidden="true" />
                </summary>
                <div className="communityBrowse__filterGroupBody">
                  <div className="communityBrowse__field dropdown-field ">
                    <label className="communityBrowse__label">Age Range</label>
                    <div className="communityBrowse__row2">
                      <Input
                        name="minAge"
                        type="number"
                        value={filters.minAge}
                        onChange={handleFilterChange}
                        placeholder="Min"
                        min="18"
                        max="100"
                      />
                      <Input
                        name="maxAge"
                        type="number"
                        value={filters.maxAge}
                        onChange={handleFilterChange}
                        placeholder="Max"
                        min="18"
                        max="100"
                      />
                    </div>
                  </div>

                  <DropdownField
                    label="Gender"
                    name="gender"
                    value={filters.gender}
                    onChange={handleFilterChange}
                    options={GENDERS}
                    placeholder="Any Gender"
                  />

                  <div className="communityBrowse__field dropdown-field ">
                    <label className="communityBrowse__label">Ethnicity</label>
                    <Input
                      name="ethnicity"
                      value={filters.ethnicity}
                      onChange={handleFilterChange}
                      placeholder="Enter ethnicity"
                    />
                  </div>
                </div>
              </details>

              <details
                className="communityBrowse__filterGroup"
                data-walkthrough="filter-group-beliefs"
              >
                <summary className="communityBrowse__filterGroupSummary">
                  <span className="communityBrowse__filterGroupTitle">Beliefs & Values</span>
                  <ChevronDown className="communityBrowse__filterGroupChevron" aria-hidden="true" />
                </summary>
                <div className="communityBrowse__filterGroupBody">
                  <DropdownField
                    label="Political Affiliation"
                    name="politicalAffiliation"
                    value={filters.politicalAffiliation}
                    onChange={handleFilterChange}
                    options={politicalAffiliationFilterOptions}
                    placeholder="All political affiliations"
                  />

                  <DropdownField
                    label="Religious Affiliation"
                    name="religiousAffiliation"
                    value={filters.religiousAffiliation}
                    onChange={handleFilterChange}
                    options={religiousAffiliationFilterOptions}
                    placeholder="All religious affiliations"
                  />

                  <div className="communityBrowse__field dropdown-field ">
                    <label className="communityBrowse__label">Sociopolitical Views</label>
                    <p className="communityBrowse__sublabel">
                      Adjust ranges to match perspectives you're searching for
                    </p>
                    <div className="communityBrowse__socioActions">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setAllSociopoliticalRelevance(true)}
                      >
                        Select all
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setAllSociopoliticalRelevance(false)}
                      >
                        Clear all
                      </Button>
                    </div>

                    {sociopoliticalQuestions.map((question) => (
                      <DualRangeSlider
                        key={question.id}
                        label={question.label}
                        leftLabel={question.leftLabel}
                        rightLabel={question.rightLabel}
                        minValue={sociopoliticalFilters[question.id].min}
                        maxValue={sociopoliticalFilters[question.id].max}
                        onMinChange={(val) => handleSociopoliticalChange(question.id, "min", val)}
                        onMaxChange={(val) => handleSociopoliticalChange(question.id, "max", val)}
                        irrelevant={sociopoliticalFilters[question.id].irrelevant}
                        onIrrelevantToggle={() => handleIrrelevantToggle(question.id)}
                        irrelevantLabel="All"
                        min={1}
                        max={7}
                        compact={true}
                      />
                    ))}
                  </div>
                </div>
              </details>

              <details className="communityBrowse__filterGroup">
                <summary className="communityBrowse__filterGroupSummary">
                  <span className="communityBrowse__filterGroupTitle">Interests</span>
                  <ChevronDown className="communityBrowse__filterGroupChevron" aria-hidden="true" />
                </summary>
                <div className="communityBrowse__filterGroupBody">
                  <div className="communityBrowse__field dropdown-field ">
                    <label className="communityBrowse__label">Interests</label>
                    <div className="communityBrowse__interestGrid">
                      {interestOptions.map((interest) => {
                        const active = filters.interests.includes(interest);
                        return (
                          <Button
                            key={interest}
                            variant={active ? "primary" : "outline"}
                            onClick={() => handleInterestToggle(interest)}
                            className="communityBrowse__pill"
                          >
                            {interest}
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </details>

              {/* ACTION BUTTONS */}
              <Button
                variant="primary"
                onClick={async () => {
                  await runSearch();
                  dispatchWalkthroughAction("filters-loaded");
                }}
                disabled={loading}
                className="communityBrowse__primaryBtn"
                data-walkthrough="apply-filters"
              >
                {loading ? "Searching…" : "Apply Filters"}
              </Button>

              <Button
                variant="outline"
                onClick={resetFilters}
                disabled={loading}
                className="communityBrowse__secondaryBtn"
              >
                Reset Filters
              </Button>

              <div className="communityBrowse__layoutToggle">
                <p className="communityBrowse__layoutToggleLabel">Card layout</p>
                <div className="communityBrowse__layoutToggleControls" role="group" aria-label="Card layout">
                  <Button
                    type="button"
                    size="sm"
                    variant={cardLayoutMode === "grid" ? "primary" : "outline"}
                    onClick={() => setCardLayoutMode("grid")}
                  >
                    Grid
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={cardLayoutMode === "stack" ? "primary" : "outline"}
                    onClick={() => setCardLayoutMode("stack")}
                  >
                    Vertical Stack
                  </Button>
                </div>
              </div>
            </div>
          </aside>


          {/* Results area */}
          <main className="communityBrowse__results" data-walkthrough="community-results">
            {loading ? (
              <div className="communityBrowse__loadingState">
                <div className="communityBrowse__spinner"></div>
                <p>Searching for connections...</p>
              </div>
            ) : results.length === 0 ? (
              <div className="communityBrowse__emptyState">
                <div className="communityBrowse__emptyIcon">
                  <Search aria-hidden="true" />
                </div>
                <h3 className="communityBrowse__emptyTitle">No Results Yet</h3>
                <p className="communityBrowse__emptyText">
                  Set your filters and click <strong>Apply Filters</strong> to explore fresh perspectives with people outside your usual interests and values.
                </p>
              </div>
            ) : (
              <>
                <div className="communityBrowse__resultsHeader">
                  <h2 className="communityBrowse__resultsTitle">
                    {totalResults} {totalResults === 1 ? "Person" : "People"} Found
                  </h2>
                  <p className="communityBrowse__resultsSubtitle">
                    {isGuestPreview
                      ? "Choose someone to message, then sign in to send your request."
                      : "Select one or more people, then send focused conversation requests."}
                  </p>
                  {!isGuestPreview && (
                    <div className="communityBrowse__batchToolbar">
                      <span className="communityBrowse__selectedCount">
                        {selectedCount} selected
                      </span>

                      <div className="communityBrowse__batchActions">
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={sendToSelected}
                          disabled={
                            loading ||
                            batchState.isSending ||
                            composerSending ||
                            selectedCount === 0
                          }
                        >
                          Request Selected
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={clearSelection}
                          disabled={batchState.isSending || composerSending || selectedCount === 0}
                        >
                          Clear
                        </Button>
                      </div>
                    </div>
                  )}
                  {!isGuestPreview && (batchState.isSending || batchSummary) && (
                    <div className="communityBrowse__batchStatus" role="status" aria-live="polite">
                      {batchState.isSending ? (
                        <p className="communityBrowse__batchStatusText">
                          Sending requests: {batchState.completed}/{batchState.total}
                        </p>
                      ) : (
                        <p className="communityBrowse__batchStatusText">
                          Batch complete ({batchSummary?.mode}): {batchSummary?.succeeded} succeeded, {batchSummary?.failed} failed.
                        </p>
                      )}
                      <div className="communityBrowse__batchProgressTrack">
                        <div
                          className="communityBrowse__batchProgressFill"
                          style={{ width: `${batchState.isSending ? batchPercent : 100}%` }}
                        />
                      </div>
                      {!batchState.isSending && batchFailures.length > 0 && (
                        <div className="communityBrowse__batchFailures">
                          <p className="communityBrowse__batchFailureText">
                            Failed recipients: {batchFailures.map((entry) => entry.recipientName).join(", ")}
                          </p>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={retryFailed}
                            disabled={batchState.isSending}
                          >
                            Retry Failed
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div
                  className={[
                    "communityBrowse__grid",
                    cardLayoutMode === "stack" ? "communityBrowse__grid--stack" : "",
                  ].join(" ").trim()}
                  ref={resultsGridRef}
                >
                  {paginatedResults.map((u, idx) => {
                    const cardId = String(u?.id ?? u?.cognitoId ?? idx);
                    const name = pickCommunityName(u);
                    const bio = pickCommunityBio(u);
                    const recipientId = pickCommunityRecipientId(u);
                    const pairState = recipientId
                      ? pairChatStateByRecipient[recipientId] || null
                      : null;
                    const pairStatus = String(pairState?.status || "").trim().toLowerCase();
                    const hasAcceptedChat = pairStatus === "accepted";
                    const hasPendingRequest = pairStatus === "pending";
                    const hasExistingPairChat = hasAcceptedChat || hasPendingRequest;
                    const isSelected = recipientId
                      ? selectedRecipientIds.includes(recipientId)
                      : false;
                    const isCurrentlyProcessing =
                      batchState.isSending &&
                      batchState.currentRecipientId === recipientId;
                    const location = u?.location?.city && u?.location?.state
                      ? `${u.location.city}, ${u.location.state}`
                      : null;
                    const occupation =
                      u?.preferences?.occupation ||
                      u?.occupation ||
                      u?.profile?.occupation ||
                      "";
                    const age = u?.demographics?.dateOfBirth
                      ? calculateAge(u.demographics.dateOfBirth)
                      : null;
                    const ageLabel = age ? `${age} years old` : "";
                    const descriptionPreview = String(bio || "").trim();
                    const hasAge = Boolean(ageLabel);
                    const hasOccupation = Boolean(occupation);
                    const hasLocation = Boolean(location);
                    const showOpenChatAction = hasAcceptedChat || hasPendingRequest;
                    const isSendBusy =
                      !showOpenChatAction &&
                      !isGuestPreview &&
                      (batchState.isSending || composerSending || messageTargetId === recipientId);
                    const isStackLayout = cardLayoutMode === "stack";

                    return (
                      <div
                        key={cardId}
                        className={[
                          "communityBrowse__card",
                          isStackLayout ? "communityBrowse__card--stack" : "communityBrowse__card--grid",
                        ].join(" ").trim()}
                        onClick={(event) => handleCardClick(event, u)}
                      >
                        <div className="communityBrowse__cardInner">
                          {!isGuestPreview && !hasExistingPairChat && recipientId && (
                            <div className="communityBrowse__cardSelectCorner">
                              <label className="communityBrowse__cardSelectLabel">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => toggleRecipientSelection(recipientId)}
                                  onClick={(event) => event.stopPropagation()}
                                  disabled={batchState.isSending || composerSending}
                                  aria-label={`Select ${name}`}
                                />
                              </label>
                            </div>
                          )}

                          <div className="communityBrowse__cardLead">
                            <div className="communityBrowse__cardMedia">
                              <ProfileAvatar user={u} name={name} />
                            </div>

                            <div className="communityBrowse__cardBody">
                              <div className="communityBrowse__cardHeader">
                                <h3 className="communityBrowse__cardName">{name}</h3>
                                <p
                                  className={[
                                    "communityBrowse__cardAgeText",
                                    hasAge ? "" : "communityBrowse__cardMetaText--empty",
                                  ].join(" ").trim()}
                                >
                                  {ageLabel || "Age not listed"}
                                </p>
                                <p
                                  className={[
                                    "communityBrowse__cardOccupation",
                                    hasOccupation ? "" : "communityBrowse__cardMetaText--empty",
                                  ].join(" ").trim()}
                                >
                                  {occupation || "Occupation not listed"}
                                </p>
                                <p
                                  className={[
                                    "communityBrowse__cardLocation",
                                    hasLocation ? "" : "communityBrowse__cardMetaText--empty",
                                  ].join(" ").trim()}
                                >
                                  <MapPin className="communityBrowse__cardLocationIcon" aria-hidden="true" />
                                  {location || "Location not listed"}
                                </p>
                              </div>
                            </div>
                          </div>

                          <div className="communityBrowse__cardSummary">
                            {descriptionPreview ? (
                              <p className="communityBrowse__cardDescription" title={bio}>
                                {descriptionPreview}
                              </p>
                            ) : (
                              <p className="communityBrowse__cardDescription communityBrowse__cardDescription--empty">
                                No summary yet.
                              </p>
                            )}
                            <div className="communityBrowse__cardActions">
                              <Button
                                variant="primary"
                                size="sm"
                                className="communityBrowse__cardBtn communityBrowse__cardBtn--message"
                                onClick={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  if (hasExistingPairChat) {
                                    openExistingPairChat(u, pairState);
                                    return;
                                  }
                                  openDirectMessage(u);
                                }}
                                disabled={!recipientId || isSendBusy}
                                data-walkthrough="send-request"
                              >
                                {showOpenChatAction ? (
                                  <>
                                    <MessageSquare className="communityBrowse__cardActionIcon" aria-hidden="true" />
                                    Open Chat
                                  </>
                                ) : !isGuestPreview && messageTargetId === recipientId
                                  ? "Opening Request..."
                                  : !isGuestPreview && isCurrentlyProcessing
                                    ? "Sending..."
                                    : (
                                      <>
                                        <MessageSquare className="communityBrowse__cardActionIcon" aria-hidden="true" />
                                        Send Request
                                      </>
                                    )}
                              </Button>

                              <Button
                                variant="outline"
                                size="sm"
                                className="communityBrowse__cardBtn communityBrowse__cardBtn--profile"
                                onClick={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  openProfile(u);
                                }}
                                disabled={isWalkthroughRestrictedToSendRequest}
                              >
                                View Profile
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {totalPages > 1 || hasMoreSearchResults ? (
                  <div className="communityBrowse__pagination" aria-label="Search results pagination">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>
                    <span
                      className="communityBrowse__paginationLabel"
                      aria-current="page"
                      aria-live="polite"
                    >
                      Page {currentPage} of {totalPages}
                      <span className="communityBrowse__paginationRange">
                        {" "}· Showing {resultsRangeStart}–{resultsRangeEnd} of {totalResults}
                        {hasMoreSearchResults ? "+" : ""}
                      </span>
                    </span>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        // On the last loaded page, ask the server for
                        // the next cursor-page before advancing — this
                        // lets the user keep paging past the initial
                        // server response without an explicit "load
                        // more" button.
                        if (currentPage === totalPages && hasMoreSearchResults && !loadingMore) {
                          await loadMoreSearchResults();
                        }
                        setCurrentPage((page) => page + 1);
                      }}
                      disabled={
                        loadingMore ||
                        (currentPage === totalPages && !hasMoreSearchResults)
                      }
                    >
                      {loadingMore && currentPage === totalPages ? "Loading…" : "Next"}
                    </Button>
                  </div>
                ) : null}
              </>
            )}
          </main>
        </div>
      </div>

      {/* Profile Modal */}
      {selectedUser && (
        <ProfileDialog
          user={selectedUser}
          onClose={closeProfile}
          calculateAge={calculateAge}
          isBlocked={blockedUserIds.includes(pickCommunityRecipientId(selectedUser))}
          onToggleBlock={isGuestPreview ? undefined : toggleBlockForSelectedUser}
          blockActionLoading={
            isGuestPreview ? false : blockActionTargetId === pickCommunityRecipientId(selectedUser)
          }
          onReportUser={
            isGuestPreview
              ? undefined
              : (pickCommunityRecipientId(selectedUser) ? openReportForSelectedUser : undefined)
          }
        />
      )}

      {!isGuestPreview && (
        <ReportDialog
          isOpen={reportDialogOpen}
          title="Report User"
          subtitle="Provide enough detail for moderators to review this profile."
          targetLabel={reportTarget?.targetLabel || "User"}
          onClose={closeReportDialog}
          onSubmit={submitUserReport}
        />
      )}

      {requestComposer && (
        <div className="communityBrowse__topicOverlay" onClick={closeRequestComposer}>
          <div
            className="communityBrowse__topicModal"
            onClick={(e) => e.stopPropagation()}
            data-walkthrough="request-composer"
          >
            <h3 className="communityBrowse__topicTitle">Conversation Request</h3>
            <p className="communityBrowse__topicSubtitle">
              {requestComposer.users.length === 1
                ? `Send a conversation request to ${pickCommunityName(requestComposer.users[0])}.`
                : `Send conversation requests to ${requestComposer.users.length} selected people.`}
            </p>

            <div className="communityBrowse__topicField">
              <label className="communityBrowse__label" htmlFor="discussion-topic-input">
                Message with your request
              </label>
              <textarea
                id="discussion-topic-input"
                className="communityBrowse__input communityBrowse__topicTextarea"
                placeholder="Write the message that starts this conversation..."
                value={discussionTopicInput}
                onChange={(e) => {
                  const nextValue = e.target.value;
                  setDiscussionTopicInput(nextValue);
                  if (
                    !hasDispatchedMessageEnteredRef.current &&
                    String(nextValue || "").trim()
                  ) {
                    hasDispatchedMessageEnteredRef.current = true;
                    dispatchWalkthroughAction("request-message-entered");
                  }
                  const localModeration = evaluateClientTextModeration({
                    fieldType: "topic",
                    text: nextValue,
                  });
                  if (localModeration.blocked) {
                    setTopicError(localModeration.feedback);
                    return;
                  }
                  if (topicError) setTopicError("");
                }}
                maxLength={DISCUSSION_TOPIC_MAX_LENGTH}
                disabled={composerSending || batchState.isSending}
                data-walkthrough="request-message"
              />
              <div className="communityBrowse__topicMeta">
                <span className="communityBrowse__topicCount">
                  {discussionTopicInput.trim().length}/{DISCUSSION_TOPIC_MAX_LENGTH}
                </span>
                {topicError || topicModeration.blocked ? (
                  <span className="communityBrowse__topicError">
                    {topicError || topicModeration.feedback}
                  </span>
                ) : null}
              </div>
            </div>

            <div className="communityBrowse__topicActions">
              <Button
                variant="ghost"
                onClick={closeRequestComposer}
                disabled={composerSending || batchState.isSending}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={submitRequestComposer}
                disabled={composerSending || batchState.isSending || isWalkthroughBlockingSend}
                data-walkthrough="request-send"
              >
                {requestComposer.mode === "single"
                  ? (composerSending ? "Sending..." : "Send Request")
                  : (batchState.isSending ? "Sending..." : "Send Requests")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function CommunityHub(props) {
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (!searchParams.has("view")) return;
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("view");
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  return (
    <div className="communityHubPage">
      <Community {...props} />
    </div>
  );
}
